import React from 'react';
import { useLocation, useSearchParams } from 'react-router-dom';
import {
  Breadcrumb,
  BreadcrumbEllipsis,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from '@/components/ui/breadcrumb';
import { generateBreadcrumbs } from '@/config/breadcrumbConfig';
import { ChevronLeft } from 'lucide-react';
import { Button } from '@/components/ui/button';

interface DynamicBreadcrumbProps {
  maxItems?: number;
  showMobileBack?: boolean;
  className?: string;
}

export function DynamicBreadcrumb({ 
  maxItems = 4, 
  showMobileBack = true,
  className = ''
}: DynamicBreadcrumbProps) {
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const breadcrumbs = generateBreadcrumbs(location.pathname, searchParams);
  
  // Breadcrumbs removed
  return null;

  const handleBack = () => {
    window.history.back();
  };

  const renderDesktopBreadcrumbs = () => {
    if (breadcrumbs.length <= maxItems) {
      const items = [];
      
      for (let i = 0; i < breadcrumbs.length; i++) {
        const crumb = breadcrumbs[i];
        
        items.push(
          <BreadcrumbItem key={`item-${crumb.href || crumb.label}-${i}`}>
            {crumb.isActive ? (
              <BreadcrumbPage className="font-medium text-foreground">
                {crumb.label}
              </BreadcrumbPage>
            ) : (
              <BreadcrumbLink 
                href={crumb.href} 
                className="text-muted-foreground hover:text-foreground transition-colors"
              >
                {crumb.label}
              </BreadcrumbLink>
            )}
          </BreadcrumbItem>
        );
        
        if (i < breadcrumbs.length - 1) {
          items.push(<BreadcrumbSeparator key={`sep-${i}`} />);
        }
      }
      
      return <>{items}</>;
    }

    // Show ellipsis when there are too many items
    const firstItems = breadcrumbs.slice(0, 1);
    const lastItems = breadcrumbs.slice(-(maxItems - 2));
    const items = [];
    
    // First items
    for (let i = 0; i < firstItems.length; i++) {
      const crumb = firstItems[i];
      items.push(
        <BreadcrumbItem key={`first-${crumb.href || crumb.label}-${i}`}>
          <BreadcrumbLink 
            href={crumb.href}
            className="text-muted-foreground hover:text-foreground transition-colors"
          >
            {crumb.label}
          </BreadcrumbLink>
        </BreadcrumbItem>
      );
      items.push(<BreadcrumbSeparator key={`first-sep-${i}`} />);
    }
    
    // Ellipsis
    items.push(<BreadcrumbItem key="ellipsis"><BreadcrumbEllipsis /></BreadcrumbItem>);
    items.push(<BreadcrumbSeparator key="ellipsis-sep" />);
    
    // Last items
    for (let i = 0; i < lastItems.length; i++) {
      const crumb = lastItems[i];
      items.push(
        <BreadcrumbItem key={`last-${crumb.href || crumb.label}-${i}`}>
          {crumb.isActive ? (
            <BreadcrumbPage className="font-medium text-foreground">
              {crumb.label}
            </BreadcrumbPage>
          ) : (
            <BreadcrumbLink 
              href={crumb.href}
              className="text-muted-foreground hover:text-foreground transition-colors"
            >
              {crumb.label}
            </BreadcrumbLink>
          )}
        </BreadcrumbItem>
      );
      
      if (i < lastItems.length - 1) {
        items.push(<BreadcrumbSeparator key={`last-sep-${i}`} />);
      }
    }
    
    return <>{items}</>;
  };

  return (
    <div className={`flex items-center gap-4 ${className}`}>
      {/* Mobile back button */}
      {showMobileBack && (
        <Button
          variant="ghost"
          size="sm"
          onClick={handleBack}
          className="md:hidden p-1 h-8 w-8"
          aria-label="Go back"
        >
          <ChevronLeft className="h-4 w-4" />
        </Button>
      )}

      {/* Desktop breadcrumb */}
      <Breadcrumb className="hidden md:flex">
        <BreadcrumbList>
          {renderDesktopBreadcrumbs()}
        </BreadcrumbList>
      </Breadcrumb>

      {/* Mobile simplified breadcrumb */}
      <div className="md:hidden text-sm text-muted-foreground">
        {breadcrumbs.length > 1 && (
          <>
            <span>{breadcrumbs[breadcrumbs.length - 2]?.label}</span>
            <span className="mx-2">/</span>
          </>
        )}
        <span className="text-foreground font-medium">
          {breadcrumbs[breadcrumbs.length - 1]?.label}
        </span>
      </div>
    </div>
  );
}